'use client';

import type { ReactNode } from 'react';
import { useEffect } from 'react';

import { trackHelpSectionClick } from './analytics';

type HelpLandingClickTrackerProps = {
    children: ReactNode;
};

const HELP_PAGE_SELECTOR = '.help-page';
const EXCLUDED_SELECTOR = '.help-page__info';
const LINK_LABEL_SELECTOR = '.sparky-c-button, .sparky-c-text-link';

const getHelpLinkSelection = (link: HTMLAnchorElement) => {
    const labelElement = link.matches(LINK_LABEL_SELECTOR) ? link : link.querySelector(LINK_LABEL_SELECTOR);

    return labelElement?.textContent ?? link.getAttribute('aria-label') ?? link.textContent ?? '';
};

export default function HelpLandingClickTracker({ children }: Readonly<HelpLandingClickTrackerProps>) {
    useEffect(() => {
        const helpPage = document.querySelector(HELP_PAGE_SELECTOR);

        if (!helpPage) {
            return;
        }

        const handleClick = (event: Event) => {
            if (!(event.target instanceof Element)) {
                return;
            }

            const link = event.target.closest('a');

            if (!link || !helpPage.contains(link) || link.closest(EXCLUDED_SELECTOR)) {
                return;
            }

            trackHelpSectionClick('help', getHelpLinkSelection(link));
        };

        helpPage.addEventListener('click', handleClick);

        return () => {
            helpPage.removeEventListener('click', handleClick);
        };
    }, []);

    return <>{children}</>;
}
